import type { Widget } from '@app/api'
import { AlertTriangle } from 'lucide-react'
import { Button, Card, Notice } from './index'
import { MemberDataConsentOffer } from './MemberDataConsent'
import { TableCard } from './Table'

/**
 * The assistant's structured output, drawn with the app's own components.
 *
 * The model returns which KIND of thing it is saying; what that kind looks like
 * is decided here. That is the whole reason the widget contract exists — an
 * assistant that could return markup could return anything, and there would be
 * no place to stand between it and the screen.
 *
 * Nothing in this file styles a surface itself. A table is a `TableCard`, a
 * record is a `Card`, a block is a `Notice` — the same ones the pages use, so a
 * token change reaches the assistant without anybody remembering it exists.
 */

export function AssistantWidget({
  widget,
  spaceId,
  onGranted,
  /**
   * Answering is only live on the last thing said. A chip row further up the
   * transcript is a question that has already been answered, and clicking it
   * would answer it again — so it renders as a record of what was asked.
   */
  onAnswer,
}: {
  widget: Widget
  /** Whose org this is — the opt-in offer is a decision made for one org. */
  spaceId: string
  /** What to do once the opt-in is granted from inside the conversation. */
  onGranted?: () => void
  onAnswer?: (answer: string) => void
}) {
  switch (widget.kind) {
    case 'table':
      return (
        <div className="asst__widget">
          {widget.title && (
            <div className="asst__widget-title">{widget.title}</div>
          )}
          <TableCard compact>
            <table>
              <thead>
                <tr>
                  {widget.columns.map((c) => (
                    <th key={c}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {widget.rows.map((row, i) => (
                  <tr key={i}>
                    {row.map((value, j) => (
                      <td
                        key={j}
                        // Each cell decides its own direction: a table of
                        // occasions has Hebrew names beside English ones, and a
                        // column direction inherited from the panel puts one of
                        // them backwards.
                        dir="auto"
                      >
                        {value}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </TableCard>
          {widget.truncated > 0 && (
            <p className="asst__widget-note">
              {widget.truncated} more not shown. Ask for a narrower set if you
              need them.
            </p>
          )}
        </div>
      )

    case 'record':
      return (
        <div className="asst__widget">
          <Card>
            <div className="asst__record-title" dir="auto">
              {widget.title}
            </div>
            <dl className="asst__record">
              {widget.fields.map((f) => (
                <div className="asst__record-row" key={f.label}>
                  <dt>{f.label}</dt>
                  <dd dir="auto">{f.value}</dd>
                </div>
              ))}
            </dl>
          </Card>
        </div>
      )

    case 'ask':
      return (
        <div className="asst__widget asst__ask">
          <p className="asst__ask-q" dir="auto">
            {widget.question}
          </p>
          <div className="asst__chips">
            {widget.options.map((option) => (
              <Button
                key={option}
                variant="secondary"
                dir="auto"
                disabled={!onAnswer}
                onClick={() => onAnswer?.(option)}
              >
                {option}
              </Button>
            ))}
          </div>
          {/* Said only while the question is live. Once it is answered the
              hint is describing a choice nobody can still make. */}
          {widget.allowOther && onAnswer && (
            <p className="asst__widget-note">Or tell me something else.</p>
          )}
        </div>
      )

    case 'blocked':
      return (
        <div className="asst__widget">
          <Notice tone="error">
            <span className="asst__blocked">
              <AlertTriangle size={14} aria-hidden />
              <span>
                <span dir="auto">{widget.reason}</span>
                {widget.instead && (
                  <span className="asst__blocked-instead" dir="auto">
                    {widget.instead}
                  </span>
                )}
                {/*
                 * The offers are named states, not free text: the consent
                 * wording for the member-data opt-in is ours and recorded, and
                 * a model improvising it would be improvising the thing the
                 * org is agreeing to. All the model chooses is which state it
                 * is in.
                 *
                 * The decision is offered HERE rather than pointed at, because
                 * the person who just hit the limit is the person who wants to
                 * settle it — and a capability you have to go and find is one
                 * that goes unused.
                 */}
                {widget.offer === 'pii_opt_in' && (
                  <MemberDataConsentOffer
                    spaceId={spaceId}
                    onGranted={onGranted}
                  />
                )}
                {/*
                 * The one offer that starts work rather than settling a
                 * setting. It says it in the person's own voice through
                 * `onAnswer`, the same path the ask chips take, so the next
                 * turn reads as them having asked for it — which they have.
                 * Live only while the turn is the last one, for the reason a
                 * chip is: an offer on a turn already answered is a button
                 * that reopens a decision nobody is making.
                 */}
                {widget.offer === 'feature_request' && onAnswer && (
                  <span className="asst__blocked-offer">
                    <Button
                      variant="secondary"
                      onClick={() =>
                        onAnswer('Yes — send that as a feature request.')
                      }
                    >
                      Send a feature request
                    </Button>
                  </span>
                )}
                {widget.offer === 'developer' && (
                  <span className="asst__blocked-instead">
                    That is a fault in the app rather than anything you did.
                    Worth reporting.
                  </span>
                )}
              </span>
            </span>
          </Notice>
        </div>
      )
  }
}
